import { useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useReprocessRuns } from '../hooks/useRuns';
import { getRunsProgress } from '../api/runs';
import { Run, RunProgress, isRunFailed, isRunSettling } from '../types/Run';
import { Session } from '../types/Session';
import { Config } from '../types/Config';
import {
  ReprocessContext,
  ReprocessContextType,
  ReprocessStatus,
  AutoCalculateStatus,
  RunReprocessInfo,
} from './reprocessContext';

import { createConfig } from '../api/configs';
import toast from 'react-hot-toast';

// Poll the lightweight /progress endpoint twice a second while a run is in flight.
const POLL_INTERVAL_MS = 500;
// A run sitting in QUEUED is waiting for a free worker — there is no live pipeline
// progress to show — so poll it gently. This is the dominant /progress flood:
// runs left 'QUEUED' by a crashed/abandoned dispatch never advance and otherwise
// poll at 500ms forever (the stall timeout is intentionally disabled for QUEUED).
const QUEUED_POLL_MS = 5000;
// A run queued far longer than any real queue wait is an abandoned reprocess —
// stop polling it entirely (its progress columns are stale until cleared server-side).
const QUEUED_STALL_MS = 30 * 60 * 1000;
// A run whose progress has not advanced for this long is treated as stalled (e.g.
// the server process died mid-reprocess and never cleared the progress columns).
const STALL_TIMEOUT_MS = 5 * 60 * 1000;
// A settling run with no progress columns yet (uploaded, not dispatched) has no
// live pipeline to follow — poll it gently, and give up after this long so an
// upload that never gets picked up cannot poll forever.
const SETTLING_POLL_MS = 5000;
const SETTLING_WATCH_MAX_MS = 10 * 60 * 1000;
// Force the stall check to re-evaluate on a timer — React Query's structural
// sharing suppresses re-renders when poll responses are identical.
const STALL_TICK_MS = 15 * 1000;
// On repeated batch-poll failures, back off exponentially (capped) so a
// struggling backend receives FEWER requests, not more — the opposite of the
// old per-run fan-out that amplified a slowdown into a request storm.
const BACKOFF_MAX_MS = 30 * 1000;

/**
 * Parse an ISO timestamp as UTC. Server datetimes can arrive without a
 * timezone designator (the `processing_started_at` column is a naive
 * `timestamp`); JS `Date.parse` reads an offset-less date-time string as
 * *browser-local* time, which skews age math by the local UTC offset and
 * can falsely trip the stall timeout the instant a run starts. If no offset
 * (`Z` or `±HH:MM`) is present, assume UTC.
 */
const parseUtcMs = (iso: string): number => {
  const hasTz = /([zZ])$|([+-]\d\d:?\d\d)$/.test(iso);
  return Date.parse(hasTz ? iso : `${iso}Z`);
};

/** A run is in flight if any of the progress side-channel columns are populated. */
const isInFlight = (p?: RunProgress): boolean =>
  !!p && (p.processing_started_at != null || p.processing_stage != null);

/**
 * Worth keeping a watch on: either a reprocess is running (progress columns
 * populated) or the run has never finished its first pass. The second case is
 * what makes this provider the single refresh authority for settling runs —
 * a freshly uploaded run has no progress columns until a worker picks it up,
 * and dropping it before then would leave its list row (and its "Processing…"
 * timestamp) frozen until a manual refresh.
 */
const isWatchable = (p?: RunProgress): boolean => isInFlight(p) || (!!p && isRunSettling(p.run_status));

// A genuine pipeline failure is the isRunFailed band (PROCESSED_ERROR). The
// legacy [3500, 4000) band (run_status = 4000 - health) is obsolete and means
// "processed, with health issues" — NOT a reprocess failure (types/Run.ts).

/** Shallow-compare two progress maps by the fields that affect rendering. */
const sameInfoMap = (a: Record<string, RunReprocessInfo>, b: Record<string, RunReprocessInfo>): boolean => {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((k) => b[k] && a[k].phase === b[k].phase && a[k].stage === b[k].stage);
};

/** Per-run bookkeeping that drives done/stalled detection but is not itself rendered. */
interface WatchMeta {
  watchedSince: number;
  seenInFlight: boolean;
  // 'submitted' — we triggered this reprocess, so show 'queued' until it starts.
  // 'probe'     — reload recovery; drop silently if the run is not actually in flight.
  mode: 'submitted' | 'probe';
}

interface ReprocessProviderProps {
  children: ReactNode;
  autoHideSuccess?: boolean;
  autoHideDelay?: number;
}

/**
 * Provider component that manages global reprocess state.
 * Ensures only one reprocess operation can run at a time across the entire application,
 * and polls the per-run `/progress` endpoint so the UI can show live progress.
 *
 * @param children - React components to wrap
 * @param autoHideSuccess - Whether to auto-hide success messages (default: true)
 * @param autoHideDelay - Delay before auto-hiding success messages in ms (default: 5000)
 *
 * @example
 * ```tsx
 * // In your App.tsx or main component
 * <ReprocessProvider>
 *   <YourApp />
 * </ReprocessProvider>
 * ```
 */
export const ReprocessProvider = ({
  children,
  autoHideSuccess = true,
  autoHideDelay = 5000,
}: ReprocessProviderProps) => {
  const [sessionsToReprocess, setSessionsToReprocess] = useState<Session[]>([]);
  const [configsToReprocess, setConfigsToReprocess] = useState<Config[]>([]);
  const [runsToReprocess, setRunsToReprocess] = useState<Run[]>([]);
  const [reprocessStatus, setReprocessStatus] = useState<ReprocessStatus>('idle');
  const [autoCalculateStatus, setAutoCalculateStatus] = useState<AutoCalculateStatus>('idle');
  const reprocessRunsMutation = useReprocessRuns();

  // ── Per-run progress polling ──────────────────────────────────────────────
  const queryClient = useQueryClient();
  const [watchedRunIds, setWatchedRunIds] = useState<string[]>([]);
  const [runInfo, setRunInfo] = useState<Record<string, RunReprocessInfo>>({});
  const metaRef = useRef<Record<string, WatchMeta>>({});
  // Mirror of runInfo, read synchronously by the poll effect and refetchInterval.
  const runInfoRef = useRef<Record<string, RunReprocessInfo>>({});
  const [stallTick, setStallTick] = useState(0);
  // Run IDs submitted by the most recent handleReprocess call — drives when the
  // global reprocessStatus may flip to 'success'/'error' (Phase 2: the PUT can
  // return 202 long before processing finishes, so the poller decides).
  const pendingBatchRef = useRef<string[]>([]);

  // The interval a single watched run *would* want, or null if it should stop
  // polling (terminal: errored, finished, or an abandoned queued stall).
  // This mirrors the previous per-query logic exactly — only the transport (one
  // batched request vs N) changes, so per-run behaviour is unchanged.
  const intervalForRun = useCallback((id: string, data: RunProgress | undefined): number | null => {
    const info = runInfoRef.current[id];
    // 'error' is terminal — stop.
    if (info && info.phase === 'error') return null;
    // A run we have seen in flight that is now cleared is finished — stop.
    if (metaRef.current[id]?.seenInFlight && data && !isWatchable(data)) return null;
    // A queue/processing stall may still recover — keep ticking slowly.
    if (info && info.phase === 'stalled') return STALL_TICK_MS;
    // Settling but not yet dispatched — no live pipeline to follow, so poll at
    // the gentle cadence rather than the 500 ms in-pipeline one.
    if (data && !isInFlight(data) && isRunSettling(data.run_status)) return SETTLING_POLL_MS;
    // A QUEUED run is waiting for a worker — poll gently, and stop once it has
    // been queued far longer than any real queue wait (an abandoned dispatch).
    if (data?.processing_stage === 'QUEUED') {
      const queuedAge = data.processing_started_at ? Date.now() - parseUtcMs(data.processing_started_at) : 0;
      return queuedAge > QUEUED_STALL_MS ? null : QUEUED_POLL_MS;
    }
    return POLL_INTERVAL_MS;
  }, []);

  // ONE batched query for every watched run, replacing the per-run useQueries
  // fan-out. Only one request is ever in flight and the next is scheduled only
  // after the previous settles, so a slow backend throttles the client instead
  // of being flooded by it. The interval is the minimum any watched run wants;
  // when none want polling (all terminal) it stops.
  const progressQuery = useQuery({
    queryKey: ['runsProgress', watchedRunIds],
    queryFn: () => getRunsProgress(watchedRunIds),
    enabled: watchedRunIds.length > 0,
    gcTime: 0,
    refetchIntervalInBackground: true,
    // Only the interval drives polling — never window-focus / reconnect bursts.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query: {
      state: { data?: RunProgress[]; status: string; fetchFailureCount: number };
    }): number | false => {
      // Back off on repeated failures so a struggling backend gets fewer hits.
      if (query.state.status === 'error') {
        const n = Math.min(query.state.fetchFailureCount || 1, 5);
        return Math.min(BACKOFF_MAX_MS, POLL_INTERVAL_MS * 2 ** n);
      }
      const byId = new Map((query.state.data ?? []).map((r) => [r.id, r]));
      let min: number | null = null;
      watchedRunIds.forEach((id) => {
        const iv = intervalForRun(id, byId.get(id));
        if (iv != null) min = min == null ? iv : Math.min(min, iv);
      });
      return min == null ? false : min;
    },
  });

  // Keep the stall check ticking even when poll responses are byte-identical.
  useEffect(() => {
    if (watchedRunIds.length === 0) return undefined;
    const handle = setInterval(() => setStallTick((t) => t + 1), STALL_TICK_MS);
    return () => clearInterval(handle);
  }, [watchedRunIds.length]);

  // Derive per-run RunReprocessInfo from poll results; drop finished/idle runs.
  useEffect(() => {
    const now = Date.now();
    const nextInfo: Record<string, RunReprocessInfo> = {};
    const finishedOk: string[] = [];
    const dropProbe: string[] = [];

    const rows = progressQuery.data;
    const byId = new Map((rows ?? []).map((r) => [r.id, r]));
    // The batch has resolved at least once → an id absent from the response is
    // genuinely not present (deleted / not-owned), not merely un-fetched.
    const batchLoaded = rows !== undefined;

    watchedRunIds.forEach((id) => {
      const meta = metaRef.current[id];
      if (!meta) return;

      // 'error' is a sticky terminal state. 'stalled' is deliberately NOT
      // sticky — a run that briefly looks stalled (a long queue wait, or a
      // stale progress column from an earlier reprocess) must be able to
      // recover to 'processing'/'complete' once a fresher poll arrives.
      const prevInfo = runInfoRef.current[id];
      if (prevInfo && prevInfo.phase === 'error') {
        nextInfo[id] = prevInfo;
        return;
      }

      const data = byId.get(id);
      const loaded = batchLoaded;
      if (isWatchable(data)) meta.seenInFlight = true;

      // A run still in the QUEUED stage is waiting for a free worker, not
      // stalled — its processing_started_at marks the *queue* time, and a
      // large batch can sit queued well past the stall timeout. Only once
      // processing genuinely starts (stage advances past QUEUED, where
      // _mark_processing_started re-stamps processing_started_at) does the
      // age of that timestamp measure real processing time.
      const isQueued = data?.processing_stage === 'QUEUED';
      const startedAge = data?.processing_started_at ? now - parseUtcMs(data.processing_started_at) : 0;
      const staleByStart = !isQueued && startedAge > STALL_TIMEOUT_MS;
      const staleByWatch = !meta.seenInFlight && now - meta.watchedSince > STALL_TIMEOUT_MS;
      // A run abandoned in QUEUED (crashed/never-picked-up dispatch) never advances;
      // cap it so it surfaces as 'stalled' and the poller stops instead of forever.
      const staleByQueued = isQueued && startedAge > QUEUED_STALL_MS;
      // Settling with no progress columns has no started_at to age off, so cap
      // the watch itself — an upload nothing ever picks up must not poll on.
      const staleBySettling = !isInFlight(data) && isWatchable(data)
        && now - meta.watchedSince > SETTLING_WATCH_MAX_MS;

      if (staleByStart || staleByWatch || staleByQueued || staleBySettling) {
        nextInfo[id] = {
          runId: id,
          phase: 'stalled',
          stage: data?.processing_stage ?? null,
          startedAt: data?.processing_started_at ?? null,
        };
        return;
      }

      if (meta.seenInFlight && data && !isWatchable(data)) {
        // Was in flight, now cleared → finished. Failures linger as 'error'.
        if (isRunFailed(data.run_status)) {
          nextInfo[id] = { runId: id, phase: 'error', stage: null, startedAt: data.processing_started_at ?? null };
        } else {
          finishedOk.push(id);
        }
        return;
      }

      if (meta.mode === 'probe' && !meta.seenInFlight && loaded && !isWatchable(data)) {
        // Reload-recovery probe found this run is absent or already settled
        // (a batch with no row for this id == not in flight).
        dropProbe.push(id);
        return;
      }
      if (meta.mode === 'probe' && !loaded) return; // probe: show nothing until the first poll resolves

      if (data && isInFlight(data)) {
        nextInfo[id] = {
          runId: id,
          phase: 'processing',
          stage: data.processing_stage,
          startedAt: data.processing_started_at ?? null,
        };
      } else {
        nextInfo[id] = { runId: id, phase: 'queued', stage: null, startedAt: null };
      }
    });

    if (!sameInfoMap(runInfoRef.current, nextInfo)) {
      runInfoRef.current = nextInfo;
      setRunInfo(nextInfo);
    }

    const drop = [...finishedOk, ...dropProbe];
    if (drop.length > 0) {
      drop.forEach((id) => delete metaRef.current[id]);
      setWatchedRunIds((prev) => prev.filter((x) => !drop.includes(x)));
    }
    if (finishedOk.length > 0) {
      finishedOk.forEach((id) => queryClient.invalidateQueries({ queryKey: ['runAndCalibration', id] }));
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['runs'] });
      // Refresh the SessionView list (phase-1 summary), the SessionSimple list and
      // any open plot tab (full payload) so a finished run updates its health /
      // status / timestamp everywhere it is listed, not just where it was started.
      queryClient.invalidateQueries({ queryKey: ['session-summary'] });
      queryClient.invalidateQueries({ queryKey: ['session-simple'] });
      queryClient.invalidateQueries({ queryKey: ['session'] });
    }
    // progressQuery.data is structurally stable across identical responses;
    // stallTick drives the periodic stall re-eval those identical responses
    // would otherwise suppress. sameInfoMap guards setRunInfo, so this won't loop.
  }, [progressQuery.data, watchedRunIds, queryClient, stallTick]);

  /**
   * Add runs to the polling set.
   * - 'submitted': we triggered the reprocess — (re)seed each run with a fresh
   *   'queued' marker, even if already tracked.
   * - 'probe': reload recovery — only touch runs NOT already tracked, so a probe
   *   can never disturb an in-flight 'submitted' watch.
   */
  const startWatching = useCallback((ids: string[], mode: 'submitted' | 'probe') => {
    const cleanIds = ids.filter((id): id is string => !!id);
    const targetIds = mode === 'probe' ? cleanIds.filter((id) => !metaRef.current[id]) : cleanIds;
    if (targetIds.length === 0) return;
    const now = Date.now();
    targetIds.forEach((id) => {
      metaRef.current[id] = { watchedSince: now, seenInFlight: false, mode };
    });
    const nextInfo = { ...runInfoRef.current };
    targetIds.forEach((id) => {
      if (mode === 'submitted') {
        nextInfo[id] = { runId: id, phase: 'queued', stage: null, startedAt: null };
      } else {
        delete nextInfo[id];
      }
    });
    runInfoRef.current = nextInfo;
    setRunInfo(nextInfo);
    setWatchedRunIds((prev) => {
      const next = [...prev];
      targetIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
  }, []);

  const watchRuns = useCallback((runIds: string[]) => startWatching(runIds, 'probe'), [startWatching]);

  /** Stop tracking runs — used when a reprocess request fails before it ever starts. */
  const unwatchRuns = useCallback((ids: string[]) => {
    ids.forEach((id) => delete metaRef.current[id]);
    const nextInfo = { ...runInfoRef.current };
    ids.forEach((id) => delete nextInfo[id]);
    runInfoRef.current = nextInfo;
    setRunInfo(nextInfo);
    setWatchedRunIds((prev) => prev.filter((id) => !ids.includes(id)));
  }, []);

  const getRunProgress = useCallback((runId: string): RunReprocessInfo | undefined => runInfo[runId], [runInfo]);

  const isRunReprocessing = useCallback(
    (runId: string): boolean => {
      const info = runInfo[runId];
      return !!info && info.phase !== 'stalled' && info.phase !== 'error';
    },
    [runInfo],
  );

  // Phase 2: the reprocess PUT can return 202 before processing finishes, so
  // the poller — not the request — decides when the batch is done. Flip the
  // global status to 'success' (or 'error' if any run failed/stalled) once
  // every run in the submitted batch has left an active phase.
  useEffect(() => {
    if (reprocessStatus !== 'processing') return undefined;
    const batch = pendingBatchRef.current;
    const isActivePhase = (id: string): boolean => {
      const info = runInfo[id];
      return !!info && (info.phase === 'queued' || info.phase === 'processing');
    };
    if (batch.some(isActivePhase)) return undefined;
    const anyFailed = batch.some((id) => {
      const info = runInfo[id];
      return !!info && (info.phase === 'error' || info.phase === 'stalled');
    });
    pendingBatchRef.current = [];
    setReprocessStatus(anyFailed ? 'error' : 'success');
    if (!anyFailed && autoHideSuccess) {
      const timer = setTimeout(() => setReprocessStatus('idle'), autoHideDelay);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [reprocessStatus, runInfo, autoHideSuccess, autoHideDelay]);

  // Computed property to determine if we should reprocess
  const shouldReprocess = sessionsToReprocess.length > 0 || configsToReprocess.length > 0 || runsToReprocess.length > 0;

  const addSessionToReprocess = (session: Session) => {
    setSessionsToReprocess((prev) => {
      // Check if session is already in the list
      if (prev.some((s) => s.id === session.id)) {
        return prev;
      }
      return [...prev, session];
    });
  };

  const addConfigToReprocess = (config: Config) => {
    setConfigsToReprocess((prev) => {
      // Check if config is already in the list
      if (prev.some((c) => c.id === config.id)) {
        return prev;
      }
      return [...prev, config];
    });
  };

  const addRunToReprocess = (runs: Run[]) => {
    setRunsToReprocess((prev) => {
      const newRuns = [...prev];
      runs.forEach((run) => {
        // Check if run is already in the list
        if (!newRuns.some((r) => r.id === run.id)) {
          newRuns.push(run);
        }
      });
      return newRuns;
    });
  };

  const removeSessionFromReprocess = (sessionId: string) => {
    setSessionsToReprocess((prev) => prev.filter((s) => s.id !== sessionId));
  };

  const removeConfigFromReprocess = (configId: string) => {
    setConfigsToReprocess((prev) => prev.filter((c) => c.id !== configId));
  };

  const removeRunFromReprocess = (runId: string) => {
    setRunsToReprocess((prev) => prev.filter((r) => r.id !== runId));
  };

  const removeAllReprocess = () => {
    setSessionsToReprocess([]);
    setConfigsToReprocess([]);
    setRunsToReprocess([]);
  };

  const handleReprocess = async (
    invalidateSession: () => void,
    immediateRuns?: Run[],
  ) => {
    // Convert all sessions, configs, and runs to a flat array of runs
    const allRunsToReprocess: Run[] = [
      ...(immediateRuns || []),
      ...runsToReprocess,
      ...sessionsToReprocess.flatMap((session) => session.configs.flatMap((config) => config.runs)),
      ...configsToReprocess.flatMap((config) => config.runs),
    ];

    if (allRunsToReprocess.length === 0) {
      return;
    }

    // Begin per-run progress tracking up front, independent of how long the
    // PUT takes — the indicators work whether the request blocks or returns
    // 202 immediately. reprocessStatus is only flipped to 'success' once the
    // poller reports every run in this batch has settled (see effect above).
    const ids = allRunsToReprocess.map((run) => run.id).filter((id): id is string => !!id);
    startWatching(ids, 'submitted');
    pendingBatchRef.current = ids;
    setReprocessStatus('processing');

    try {
      const result = await reprocessRunsMutation.mutateAsync({
        runsToReprocess: allRunsToReprocess,
      });

      // Clear all reprocess lists once the request has been accepted
      removeAllReprocess();
      invalidateSession();

      // Phase 2: a non-blocking reprocess returns 202 with any rejected runs
      // (already in flight). Surface those; accepted runs are now polled.
      const rejected = result && Array.isArray(result.rejected) ? result.rejected : [];
      if (rejected.length > 0) {
        toast(`${rejected.length} run${rejected.length > 1 ? 's' : ''} already reprocessing`);
      }
    } catch (e) {
      console.error(e);
      unwatchRuns(ids);
      pendingBatchRef.current = [];
      setReprocessStatus('error');
    }
  };

  const handleAutoCalculate = async (session: Session, invalidateSession: () => void) => {
    setAutoCalculateStatus('processing');
    try {
      await createConfig(
        JSON.stringify({
          team_id: session.team_id,
          testsession_id: session.id,
          autocalibrate: true,
        }),
      );
      setAutoCalculateStatus('success');
      invalidateSession();

      // Auto-hide success message if enabled
      if (autoHideSuccess) {
        setTimeout(() => {
          setAutoCalculateStatus('idle');
        }, autoHideDelay);
      }
    } catch (e) {
      console.error(e);
      setAutoCalculateStatus('error');
    }
  };

  const resetStatus = () => {
    setReprocessStatus('idle');
  };

  const resetAutoCalculateStatus = () => {
    setAutoCalculateStatus('idle');
  };

  const value: ReprocessContextType = {
    sessionsToReprocess,
    configsToReprocess,
    runsToReprocess,
    shouldReprocess,
    reprocessStatus,
    autoCalculateStatus,
    isProcessing: reprocessStatus === 'processing',
    isAutoCalculating: autoCalculateStatus === 'processing',
    addSessionToReprocess,
    addConfigToReprocess,
    addRunToReprocess,
    removeSessionFromReprocess,
    removeConfigFromReprocess,
    removeRunFromReprocess,
    removeAllReprocess,
    handleReprocess,
    handleAutoCalculate,
    resetStatus,
    resetAutoCalculateStatus,
    getRunProgress,
    isRunReprocessing,
    watchRuns,
  };

  return <ReprocessContext.Provider value={value}>{children}</ReprocessContext.Provider>;
};

// Nothing is re-exported from here on purpose. Re-exporting the context value
// put this module back on the import path of its own consumers, which is the
// cycle ./reprocessContext was split out to break — import from there instead.
